import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { apiGet } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { sortByLabel } from "../../utils/sortOptions";

let cachedEmployees = null;
let inflight = null;

function unwrapEmployees(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

export async function loadEmployeeDirectory() {
  if (cachedEmployees) return cachedEmployees;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await apiGet(endpoints.selfService.directory);
      const rows = unwrapEmployees(res);
      if (rows.length) {
        cachedEmployees = rows;
        return rows;
      }
    } catch (_) {
      /* fall through to admin roster */
    }
    try {
      const res = await apiGet(endpoints.employees.list);
      cachedEmployees = unwrapEmployees(res);
    } catch (_) {
      cachedEmployees = [];
    }
    return cachedEmployees;
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

export function employeeOptionLabel(e) {
  if (!e) return "";
  if (typeof e === "string") return e;
  const name = e.name || "";
  const id = e.employeeid || e.id || "";
  if (name && id) return `${name} (${id})`;
  return name || id || "";
}

function asIdList(value, multiple) {
  if (multiple) {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (typeof value === "string" && value.trim()) {
      return value.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }
  return value ? [String(value)] : [];
}

export default function EmployeeSelect({
  label = "Employee",
  value,
  onChange,
  multiple = false,
  required = false,
  error = false,
  helperText = "",
  disabled = false,
  size = "small",
  fullWidth = true,
  excludeIds = [],
  roles,
  managersOnly = false,
  placeholder = "Search name or ID…",
  sx,
}) {
  const [employees, setEmployees] = useState(cachedEmployees || []);
  const [loading, setLoading] = useState(!cachedEmployees);

  useEffect(() => {
    let cancelled = false;
    setLoading(!cachedEmployees);
    loadEmployeeDirectory()
      .then((rows) => {
        if (!cancelled) setEmployees(rows);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const exclude = useMemo(() => new Set((excludeIds || []).filter(Boolean).map(String)), [excludeIds]);
  const roleSet = useMemo(() => {
    const list = managersOnly ? ["admin", "hr", "manager"] : roles;
    return list?.length ? new Set(list.map((r) => String(r).toLowerCase())) : null;
  }, [roles, managersOnly]);

  const selectedIds = asIdList(value, multiple);

  const options = useMemo(() => {
    const byId = new Map();
    for (const e of employees) {
      const id = e?.employeeid;
      if (!id || exclude.has(String(id))) continue;
      if (roleSet && !roleSet.has(String(e.role || "").toLowerCase()) && !selectedIds.includes(String(id))) {
        continue;
      }
      byId.set(String(id), e);
    }
    for (const id of selectedIds) {
      if (!byId.has(id) && !exclude.has(id)) {
        byId.set(id, { employeeid: id, name: id });
      }
    }
    return sortByLabel([...byId.values()], employeeOptionLabel);
  }, [employees, exclude, roleSet, selectedIds]);

  const selected = multiple
    ? selectedIds.map((id) => options.find((o) => String(o.employeeid) === id)).filter(Boolean)
    : options.find((o) => String(o.employeeid) === selectedIds[0]) || null;

  return (
    <Autocomplete
      multiple={multiple}
      options={options}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      value={selected}
      onChange={(_, val) => {
        if (multiple) {
          const list = Array.isArray(val) ? val : [];
          onChange?.(list.map((e) => e.employeeid), list);
        } else {
          onChange?.(val?.employeeid || "", val || null);
        }
      }}
      getOptionLabel={employeeOptionLabel}
      isOptionEqualToValue={(a, b) => String(a?.employeeid) === String(b?.employeeid)}
      filterOptions={(opts, state) => {
        const q = String(state.inputValue || "").trim().toLowerCase();
        if (!q) return opts;
        return opts.filter((e) =>
          [e.name, e.employeeid, e.email, e.designation, e.department, e.role]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q)
        );
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.employeeid}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.25 }}>
              {option.name}
              <Typography component="span" sx={{ fontWeight: 600, color: "text.secondary", ml: 0.75, fontSize: 12.5 }}>
                ({option.employeeid})
              </Typography>
            </Typography>
            {(option.designation || option.department || option.role) && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {[option.designation, option.department, option.role].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          required={required}
          error={error}
          helperText={helperText}
          label={label}
          placeholder={placeholder}
        />
      )}
    />
  );
}
